import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

const WhenHomeTherapyTeamsGrowArticle = () => (
    <Box>
        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.65rem', md: '2rem' } }}>
            When Home Therapy Teams Grow: What Breaks at Scale
        </Typography>
        <Typography color="text.secondary" fontWeight={600} paragraph sx={{ fontStyle: 'italic' }}>
            The system that worked at five therapists won&apos;t survive fifteen.
        </Typography>
        <Typography color="text.secondary" paragraph>
            <strong>TL;DR:</strong> When a home therapy team grows past 10 to 12 therapists, three things break in a predictable
            order: session verification goes first, documentation consistency follows, payout accuracy comes last. This
            sequence isn&apos;t random. It&apos;s structural, and it shows up at the same team size across growing rehab
            centres. The founder absorbs the operational overflow personally until that stops being viable. Centres that put
            the right infrastructure in place before hitting that point spend significantly less than the ones that wait
            until the disputes are already chronic.
        </Typography>
        <Typography color="text.secondary" paragraph>
            When a home therapy team grows past 10 to 12 therapists, the first thing that breaks is session verification.
            Documentation inconsistency follows. Payout disputes come last. This order isn&apos;t guesswork. It&apos;s a pattern
            confirmed across growing rehab centres, and it happens at the same point in almost every growing rehab centre.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What holds at small scale
        </Typography>
        <Typography color="text.secondary" paragraph>
            At five or six therapists, a founder or ops head can hold the whole operation in their head. They know which
            therapist covers which area, roughly how many sessions each person runs, who&apos;s reliable with documentation and
            who needs chasing. That personal knowledge fills the gaps the system doesn&apos;t.
        </Typography>
        <Typography color="text.secondary" paragraph>
            WhatsApp works because the volume&apos;s low enough that nothing stays buried. A missed confirmation is an
            exception, not a pattern. Payout calculations are messy but manageable.
        </Typography>
        <Typography color="text.secondary" paragraph>
            That&apos;s not the system working well. That&apos;s the founder compensating for it.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            The inflection point: 10 to 12 therapists
        </Typography>
        <Typography color="text.secondary" paragraph>
            Consider the same founder at two different team sizes.
        </Typography>
        <Typography color="text.secondary" paragraph>
            At five therapists, a typical day involves a handful of session confirmations over WhatsApp, one or two
            follow-ups, maybe a call about a patient who wasn&apos;t home. It takes 30 to 45 minutes across the day and
            doesn&apos;t demand dedicated attention.
        </Typography>
        <Typography color="text.secondary" paragraph>
            At 15 therapists, that same day looks entirely different. Twelve to fifteen confirmation messages, two of which
            contradict each other, one session from three days ago still unresolved. The reconciliation that used to take two
            days is now a week-long back-and-forth. Month-end stops being a calculation. It becomes an investigation that
            bleeds into the following week.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The team tripled. The operational burden grew much faster than that.
        </Typography>
        <Typography color="text.secondary" paragraph>
            That shift happens at 10 to 12 therapists. Not 20, not 30. They can no longer hold the full picture in their
            head, the gaps the system was never filling become real problems, and the breakdown sequence starts.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What breaks, and in what order
        </Typography>
        <Typography color="text.secondary" paragraph>
            Session verification goes first. There&apos;s no mechanism to confirm a home visit independently of the
            therapist&apos;s own report. At small scale, an unverified session is something you can chase down. At 12 or 15
            therapists, it becomes routine. By month-end, sessions are in the payout calculation that nobody can confirm
            actually happened.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Documentation suffers next. Notes that used to be completed same-day start getting filled in retrospectively, end
            of week, sometimes later. Fields get skipped. Records that need to hold up for insurance purposes become
            inconsistent across the team, not because therapists are careless, but because there&apos;s no structure
            enforcing completion at the right moment.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Payout disputes arrive last. Reconciliation stretches across weeks. The admin&apos;s spending a large chunk of
            every month reconstructing what happened rather than calculating what&apos;s owed, and what used to get resolved in
            one call is now a multi-day negotiation with no verified record to anchor it.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Here&apos;s why the sequence matters: by the time the disputes surface, the root cause is weeks old. Fixing disputes
            without fixing verification treats the symptom. The problem started at step one.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/payout-disputes-home-rehab-fix">
                Payout Disputes in Home Rehab: Can Your System Fix Them?
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why the founder ends up carrying it
        </Typography>
        <Typography color="text.secondary" paragraph>
            Without infrastructure, someone fills the gap. It&apos;s almost always the founder.
        </Typography>
        <Typography color="text.secondary" paragraph>
            There are more calls to confirm sessions, more manual checks before the payout cycle closes, more time spent as
            referee in disputes that shouldn&apos;t have reached that point.
        </Typography>
        <Typography color="text.secondary" paragraph>
            That time comes from somewhere. Clinical oversight, business development, or simply not being in the weeds
            constantly. A founder managing a 15-person field team who&apos;s still reconciling WhatsApp threads on a Friday
            evening isn&apos;t running the business. They&apos;re holding it together with duct tape. As the team grows, that
            load grows with it.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why adding process doesn&apos;t fix it
        </Typography>
        <Typography color="text.secondary" paragraph>
            The usual response is to tighten things up: a daily reporting format, mandatory confirmation messages by a fixed
            time, a more detailed spreadsheet.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Symptoms ease for a while. Nothing actually changes, because reports without independent verification aren&apos;t
            records. They&apos;re claims. More reporting requirements produce more self-reporting, more admin burden on both
            sides, and still no verified record at the end of the month.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What needs to be in place before the inflection point
        </Typography>
        <Typography color="text.secondary" paragraph>
            Centres running field teams of 15 to 20 therapists without constant operational friction tend to share one thing:
            verification&apos;s happening at point of delivery, not through a separate confirmation step afterwards.
        </Typography>
        <Typography color="text.secondary" paragraph>
            When a therapist completes a session, a structured record is created then and there. Who visited, which patient,
            when, what was documented. That record feeds directly into documentation and payout calculation. No separate
            confirmation step, no month-end reconstruction, no one calling therapists to cross-check what already happened.
            That&apos;s what operational infrastructure looks like at this scale.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/why-whatsapp-breaks-field-operations-system">Why WhatsApp Breaks as a Field Operations System</Link>.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            The cost of waiting
        </Typography>
        <Typography color="text.secondary" paragraph>
            Most founders approaching this point can feel that something&apos;s getting harder. Waiting feels reasonable. See
            if the team settles, hire one more admin, manage manually a bit longer.
        </Typography>
        <Typography color="text.secondary" paragraph>
            What makes that expensive is the compounding. Unverified sessions produce documentation gaps, those gaps produce
            payout disputes, and months of financial leakage accumulate before things are bad enough to force a decision. By
            then the cost of fixing it&apos;s significantly higher than the cost of getting ahead of it.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/session-verification-payout-cycle-home-rehab">
                How Session Verification Changes Your Payout Cycle
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why rehab centres use meDZhub at this stage
        </Typography>
        <Typography color="text.secondary" paragraph>
            The breakdown sequence in this article, session verification failing first, documentation drifting next, payout
            disputes arriving last, isn&apos;t something you can fix by changing how your team communicates. It&apos;s a
            structural problem. The system has no way to verify what happened in the field independently of the people who were
            there.
        </Typography>
        <Typography color="text.secondary" paragraph>
            meDZhub was built specifically for this gap. It creates a verified session record at point of delivery, enforces
            documentation structure on-site, and links both directly to payout calculation. There&apos;s no separate
            confirmation step, no month-end reconstruction, no disputes over what someone claims they did.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Centres that put this in place around the 10-therapist mark don&apos;t go through the breakdown sequence at all.
            The ones that wait until they&apos;re already absorbing disputes and chasing documentation are fixing a problem
            that&apos;s been compounding for months.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Your team&apos;s operations only get more complex as it grows. If you&apos;re approaching 10 therapists and
            already feeling the friction, it&apos;s worth seeing what a verified session record looks like in practice before
            the breakdown starts.
        </Typography>
        <Button component={Link} to="/demo" variant="contained" sx={{ mt: 1, mb: 4 }} data-gtm="Blog-when-teams-grow-demo">
            Book a demo
        </Button>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 2, mb: 2 }}>
            Frequently asked questions
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            At what point does a home therapy team outgrow WhatsApp and manual systems?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Consistently at 10 to 12 therapists. Below that, the founder or ops head can keep a working picture of the
            operation without a formal system. Above it, there are too many moving parts to track without independent
            verification.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What are the first things that break when a home rehab team scales past 10 therapists?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Session verification breaks first. Therapists complete sessions with no independent confirmation they happened.
            Documentation follows, with notes filled in retrospectively and quality dropping across the team. Payout disputes
            come last, by which point the problem&apos;s been compounding for weeks.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            Why don&apos;t stricter reporting requirements solve the problem?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Because more reporting doesn&apos;t produce verification. Self-reported data is still self-reported, regardless
            of how much you collect. The problem is the absence of an independent record, and adding reporting steps doesn&apos;t
            create one.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What does operational infrastructure look like for a team of 15 to 20 field therapists?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Verification at point of delivery, structured documentation completed on-site, payouts calculated from confirmed
            session data. The ops head has visibility without manual checks. Month-end is a calculation, not a reconstruction.
        </Typography>
    </Box>
);

export default WhenHomeTherapyTeamsGrowArticle;
